/**
 * RootErrorBoundary — the last line of defence ABOVE the router, mounted in
 * `main.tsx` wrapping the whole provider tree.
 *
 * The router's `__root` `errorComponent` (RouteError) only catches throws
 * within the route subtree. An uncaught throw in the providers that sit
 * *above* `<RouterProvider />` — AuthProvider, QueryClientProvider, the
 * StepUp / branding overlays mounted in `__root`, or the router's own
 * bootstrap — would otherwise white-screen with nothing rendered. This
 * class boundary catches those and renders a branded, accessible fallback
 * (light theme per the product UX spec §2.3) with a reload affordance.
 *
 * It is intentionally self-contained: it must not depend on the router or
 * any data provider, because by the time it renders one of those may be the
 * very thing that threw. Inline styles + design-system CSS variables only.
 *
 * Lives in its own module (rather than inline in `main.tsx`) so it can be
 * unit-tested without triggering `main.tsx`'s `createRoot` side effect.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  hasError: boolean;
}

export class RootErrorBoundary extends Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
  constructor(props: RootErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): RootErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log to the console for triage; a telemetry sink can subscribe later.
    // We never render the raw error to the user (no infra / stack leak).
    console.error('Root error boundary caught an unrecoverable error:', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return <RootErrorFallback />;
  }
}

/**
 * Branded fallback for {@link RootErrorBoundary}. Light theme, single h1,
 * reload CTA. `role="alert"` so assistive tech announces it immediately.
 * Exported so the boundary test can assert its content directly.
 */
export function RootErrorFallback() {
  return (
    <div
      role="alert"
      data-testid="root-error-fallback"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        background: 'var(--bg, #f7f5f1)',
        fontFamily: 'var(--sans, system-ui, sans-serif)',
      }}
    >
      <div
        style={{
          background: 'var(--surface, #ffffff)',
          border: '1px solid var(--hairline, #e5e1d8)',
          borderRadius: 'var(--r-xl, 16px)',
          padding: '48px 40px',
          width: '100%',
          maxWidth: 520,
          textAlign: 'center',
        }}
      >
        <p
          className="eyebrow"
          style={{ margin: '0 0 20px' }}
        >
          Something went wrong
        </p>
        <h1
          style={{
            fontFamily: 'var(--serif, Georgia, serif)',
            fontStyle: 'italic',
            fontWeight: 300,
            fontSize: 'clamp(28px, 5vw, 44px)',
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            color: 'var(--ink, #1a1a1a)',
            margin: '0 0 18px',
          }}
        >
          We hit an unexpected error
        </h1>
        <p
          style={{
            fontSize: 16,
            lineHeight: 1.6,
            color: 'var(--ink-3, #5c5c5c)',
            margin: '0 0 28px',
          }}
        >
          Your data is safe. Nothing has been changed. Reloading the page
          usually clears it. If it keeps happening, contact your administrator.
        </p>
        <button
          type="button"
          onClick={() => {
            if (typeof window !== 'undefined') window.location.reload();
          }}
          style={{
            background: 'var(--accent-400, #c4962a)',
            color: 'var(--primary-900, #11231f)',
            padding: '12px 24px',
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--sans, system-ui, sans-serif)',
          }}
        >
          Reload the page
        </button>
      </div>
    </div>
  );
}
